"use client";

import { PraetorSdk } from "@wkcz/praetor-sdk";
import { appEvents } from "@/lib/events";
import { ClientLogger } from "@/lib/clientLogger";

let praetorInstance: any = null;
let cachedToken: string | null = null;
let tokenExpiresAt: number = 0;

async function getPraetorInstance(): Promise<PraetorSdk> {
  if (praetorInstance) return praetorInstance;
  const { Praetor } = await import("@wkcz/praetor-sdk");
  praetorInstance = new Praetor();
  return praetorInstance;
}

export function openPraetorCaseDocument(documentId: string): void {
  getPraetorInstance()
    .then((praetor) => {
      praetor as PraetorSdk;
      praetor.actions.openDocument({ id: documentId }).catch((e: any) => {
        ClientLogger.error("Failed to open Praetor case document", {
          error: e, component: "openPraetorCaseDocument", metadata: { documentId },
        });
      });
    });
}

export async function selectPraetorDocuments(
  caseId: string | null,
): Promise<{ caseId: string | null; documents: Array<{ id: string; name: string }> }> {
  const praetor = await getPraetorInstance();
  const result = await praetor.actions.selectDocuments({ caseId });
  return {
    caseId: result?.caseId ?? null,
    documents: result?.documents ?? [],
  };
}

export async function selectPraetorCase(): Promise<{ id: string; displayName: string } | null> {
  const praetor = await getPraetorInstance();
  const result = await praetor.actions.selectCase();
  if (!result?.id) return null;
  return { id: result.id, displayName: result.displayName ?? "" };
}

export async function getAccessToken(): Promise<string>
{
  const now = Date.now();
  if (cachedToken && tokenExpiresAt > now + 30_000) {
    return cachedToken;
  }

  let praetor: any;
  try {
    praetor = await getPraetorInstance();
  } catch (e) {
    ClientLogger.error("Failed to get Praetor instance", { error: e instanceof Error ? e : undefined, component: "getAccessToken" });
    throw e;
  }

  let pactToken: string;
  try {
    pactToken = await praetor.getPactToken();
  } catch (e) {
    ClientLogger.error("getPactToken() threw", { error: e instanceof Error ? e : undefined, component: "getAccessToken" });
    throw e;
  }

  // TODO: verify this is the right way to detect non-praetor environment
  if (pactToken === 'mock-embedded-context-token') {
    ClientLogger.debug("Got mock token — not in Praetor environment", { component: "getAccessToken" });
    throw new Error("Not in praetor environment");
  }

  cachedToken = pactToken;
  tokenExpiresAt = now + 240 * 1000; // Assume token is valid for 4 minutes; refresh it 30 seconds before expiry to be safe

  return pactToken;
}

export function getAssistantBaseUrl(): string
{
  return import.meta.env.VITE_ASSISTANT_BASE_URL ?? (() => { throw new Error("VITE_ASSISTANT_BASE_URL is not defined"); })();
}

const apiUrl = getAssistantBaseUrl();

// --- Dev admin mode ---
// In non-production builds, a floating toggle can enable X-Admin-Mode: true on all API requests.
// The backend's CookieSessionAuthenticationHandler injects the tenant.admin claim when this
// header is present and the environment is Development.
const DEV_ADMIN_STORAGE_KEY = "pa_dev_admin_mode";

function isProductionEnvironment(): boolean {
  return import.meta.env.VITE_IS_PRODUCTION !== "false";
}

export function getDevAdminMode(): boolean {
  if (isProductionEnvironment()) return false;
  return localStorage.getItem(DEV_ADMIN_STORAGE_KEY) === "true";
}

export function setDevAdminMode(enabled: boolean): void {
  if (isProductionEnvironment()) return;
  if (enabled) {
    localStorage.setItem(DEV_ADMIN_STORAGE_KEY, "true");
  } else {
    localStorage.removeItem(DEV_ADMIN_STORAGE_KEY);
  }
}

// Track whether the SPA has seen at least one 503+X-IDP-Unavailable response
// since the last successful /api/* call. Lets us emit `idp-restored` on the
// first recovered request without spamming the bus on every 2xx.
let idpOutageSeen = false;

/**
 * Authenticated fetch.
 *
 * Auth model after the BFF redesign:
 *  - Bearer mode (WebView2): {@link getAccessToken} returns a fresh PACT;
 *    we attach `Authorization: Bearer <token>` per request. The backend's
 *    Hybrid handler validates + exchanges per request.
 *  - Basic mode (standalone): the SPA holds NO credentials. The
 *    `pa_session` httpOnly cookie travels automatically as a same-origin
 *    request and the backend's Cookie scheme authenticates it.
 *
 * 401 → full-page redirect to /login (returnUrl preserved).
 * 403 → modal via `forbidden-action` event; caller can read response.
 * 402 → `quota-exceeded` event (existing).
 * 503 with `X-IDP-Unavailable: 1` → `idp-unavailable` event (Phase 4 banner).
 */
export async function apiAuthFetch(pathAndQuery: string, options: RequestInit = {}): Promise<Response> {
  const url = `${apiUrl.endsWith("/") ? apiUrl : apiUrl + "/"}${pathAndQuery.startsWith("/") ? pathAndQuery.slice(1) : pathAndQuery}`;

  // Breadcrumb for every API call (skip telemetry endpoint to avoid recursion)
  if (!pathAndQuery.includes("/telemetry")) {
    ClientLogger.addBreadcrumb(`${options.method ?? "GET"} ${pathAndQuery}`);
  }

  // Try Bearer first; if the SDK isn't available (standalone browser) the
  // request goes out with the cookie only.
  let authHeader: string | null = null;
  try {
    const token = await getAccessToken();
    authHeader = `Bearer ${token}`;
  } catch {
    authHeader = null;
  }

  const headers: Record<string, string> = { ...(options.headers as Record<string, string>) };
  if (authHeader) headers.Authorization = authHeader;
  if (getDevAdminMode()) headers["X-Admin-Mode"] = "true";

  let response: Response;
  try {
    response = await fetch(url, {
      ...options,
      credentials: "same-origin",
      headers,
    });
  } catch (err) {
    // Network-level failure (offline, DNS, CORS, etc.)
    if (!pathAndQuery.includes("/telemetry")) {
      ClientLogger.error("Network error on API call", {
        error: err,
        action: pathAndQuery,
        component: "apiAuthFetch",
      });
    }
    throw err;
  }

  if (response.status === 401) {
    try {
      const body = await response.clone().json();
      ClientLogger.warn("Authentication failed", {
        action: pathAndQuery,
        component: "apiAuthFetch",
        metadata: { status: response.status, detail: body?.detail ?? body?.title },
      });
    } catch (e) {
      ClientLogger.warn("Auth error response unparseable", { error: e, action: pathAndQuery });
    }
    const returnUrl = encodeURIComponent(window.location.pathname + window.location.search);
    window.location.href = `/login?returnUrl=${returnUrl}`;
    return response;
  }

  if (response.status === 403) {
    let detail: string | undefined;
    try {
      const body = await response.clone().json();
      detail = body?.detail ?? body?.title;
    } catch {
      // ignore — emit the event anyway with no detail
    }
    ClientLogger.warn("Forbidden action", {
      action: pathAndQuery,
      component: "apiAuthFetch",
      metadata: { status: 403, detail },
    });
    appEvents.emit("forbidden-action", { detail });
    return response;
  }

  if (response.status === 402) {
    appEvents.emit("quota-exceeded");
    return response;
  }

  if (response.status === 503 && response.headers.get("X-IDP-Unavailable") === "1") {
    idpOutageSeen = true;
    appEvents.emit("idp-unavailable");
    return response;
  }

  if (response.ok && idpOutageSeen) {
    idpOutageSeen = false;
    appEvents.emit("idp-restored");
  }

  // Log non-success responses (skip handled branches above + telemetry endpoint)
  if (!response.ok) {
    if (!pathAndQuery.includes("/telemetry")) {
      ClientLogger.warn("API call returned error status", {
        action: pathAndQuery,
        component: "apiAuthFetch",
        metadata: { status: response.status, statusText: response.statusText },
      });
    }
  }

  return response;
}
